import { DEFAULT_TEMPLATES, fillTemplate } from '@iws/pos-devices'
import { Download, Printer } from 'lucide-react'

import { useEffect, useState } from 'react'

import { PosReportModel, type CashInOut, type ShiftReport } from '@/http/models/PosReport'
import { useBusiness } from '@/contexts/BusinessContext'
import { downloadCsv } from '@/lib/exportRows'
import { printSheet } from '@/lib/printSheet'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'

import { formatDateTime, signed } from './format'

interface Props {
  shiftId: string | null
  terminalName: string
  shiftNo?: string
  onClose: () => void
}

const Panel = ({
  title,
  rows,
  total,
  after = [],
  foot,
}: {
  title: string
  rows: [string, string][]
  total: [string, string]
  after?: [string, string, string?][]
  foot?: string
}) => (
  <section className="rounded-md border border-border p-4">
    <h3 className="mb-2 text-sm font-semibold text-ink">{title}</h3>
    <dl className="divide-y divide-border-subtle text-sm">
      {rows.map(([label, value], i) => (
        <div key={`${label}-${i}`} className="flex justify-between gap-4 py-1.5">
          <dt className="min-w-0 text-muted-foreground">{label}</dt>
          <dd className="tabular-nums text-ink">{value}</dd>
        </div>
      ))}
      <div className="flex justify-between gap-4 py-1.5 font-semibold text-ink">
        <dt>{total[0]}</dt>
        <dd className="tabular-nums">{total[1]}</dd>
      </div>
      {after.map(([label, value, tone]) => (
        <div key={label} className={`flex justify-between gap-4 py-1.5 ${tone ? `font-semibold ${tone}` : ''}`}>
          <dt className={tone ? '' : 'text-muted-foreground'}>{label}</dt>
          <dd className="tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
    {foot && <p className="mt-2 text-xs text-muted-foreground">{foot}</p>}
  </section>
)

/** The server's totals for one shift (`GET /pos-report/shifts?shift_id=`), the same figures as the till's Z report. */
export const ShiftReportDialog = ({ shiftId, terminalName, shiftNo, onClose }: Props) => {
  const { formatCurrency } = useBusiness()
  const [report, setReport] = useState<ShiftReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [cashMoves, setCashMoves] = useState<CashInOut[]>([])

  useEffect(() => {
    if (!shiftId) return
    setLoading(true)
    setReport(null)
    setCashMoves([])
    PosReportModel.getShiftReports({ shift_id: shiftId, length: 1 }).then((res) => {
      const found = res.success ? (res.data?.result?.[0] ?? null) : null
      setReport(found)
      setLoading(false)
      // The cash list has no shift filter; the terminal's recent moves are fetched and this shift's kept.
      if (found && (found.cash_in || found.cash_out)) {
        PosReportModel.getCashInOuts({ terminal_id: found.terminal_id, length: 200 }).then((cash) => {
          if (cash.success) setCashMoves((cash.data?.result ?? []).filter((c) => c.shift_id === found.shift_id))
        })
      }
    })
  }, [shiftId])

  const period = report ? `${formatDateTime(report.opened_at)} – ${report.closed_at ? formatDateTime(report.closed_at) : 'open'}` : ''
  const fileName = report ? `shift-${terminalName}-${report.opened_at.slice(0, 10)}`.replace(/[^\w-]+/g, '-') : 'shift'

  const reprint = () =>
    report &&
    printSheet(
      fillTemplate(DEFAULT_TEMPLATES.z_report, {
        shop: { name: '' },
        shift: {
          title: report.closed_at ? 'Z REPORT' : 'X REPORT',
          counter: terminalName,
          cashier: '',
          opened: formatDateTime(report.opened_at),
          closed: report.closed_at ? formatDateTime(report.closed_at) : 'open',
          rows: rows.map(([label, value]) => ({ label, value })),
        },
      }),
      report.closed_at ? 'Z report' : 'X report'
    )

  const exportCsv = () =>
    downloadCsv(fileName, ['Item', 'Amount'], [...(shiftNo ? [['Shift', shiftNo]] : []), ['Terminal', terminalName], ['Period', period], ...rows])

  const rows: [string, string][] = report
    ? [
        ['Orders', String(report.order_count)],
        ['Cash sales', formatCurrency(report.sales.cash)],
        ['Card sales', formatCurrency(report.sales.card)],
        ['Bank sales', formatCurrency(report.sales.bank)],
        ['QR sales', formatCurrency(report.sales.qr)],
        ['Credit sales', formatCurrency(report.sales.credit)],
        ['Refunds', formatCurrency(report.refunds)],
        ['Credit repayments', formatCurrency(report.repayments)],
        ['Opening cash', formatCurrency(report.opening_balance)],
        ['Cash in', formatCurrency(report.cash_in)],
        ['Cash out', formatCurrency(report.cash_out)],
        ['Expected cash', formatCurrency(report.expected_cash)],
        ['Counted cash', report.closing_balance === null ? '—' : formatCurrency(report.closing_balance)],
        ['Difference', signed(report.discrepancy, formatCurrency)],
      ]
    : []

  return (
    <Dialog open={!!shiftId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{shiftNo ? `Shift ${shiftNo} · ${terminalName}` : `Shift on ${terminalName}`}</DialogTitle>
          <DialogDescription>
            {report ? period : 'Sales and cash for this shift.'}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : !report ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No report for this shift yet. It is built when the shift's records sync.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-2">
              <Panel
                title="Sales by payment method"
                rows={[
                  ['Cash', formatCurrency(report.sales.cash)],
                  ['Card', formatCurrency(report.sales.card)],
                  ['Bank', formatCurrency(report.sales.bank)],
                  ['QR', formatCurrency(report.sales.qr)],
                  ['Credit', formatCurrency(report.sales.credit)],
                  ['Refunds', formatCurrency(report.refunds ? -report.refunds : 0)],
                ]}
                total={['Net sales', formatCurrency(Object.values(report.sales).reduce((a, b) => a + b, 0) - report.refunds)]}
                foot={`${report.order_count} orders · credit repaid ${formatCurrency(report.repayments)}`}
              />
              <Panel
                title="Cash drawer"
                rows={[
                  ['Opening float', formatCurrency(report.opening_balance)],
                  ['+ Cash sales', formatCurrency(report.sales.cash)],
                  ['− Cash refunds', formatCurrency(report.cash_refunds)],
                  ['+ Cash repaid', formatCurrency(report.cash_repayments)],
                  ...(cashMoves.length
                    ? cashMoves.map((c): [string, string] => [
                        `${c.type === 'cash_in' ? '+ Cash in' : '− Cash out'}${c.remark ? ` (${c.remark})` : ''} · ${formatDateTime(c.created_at).slice(-5)}`,
                        formatCurrency(c.amount),
                      ])
                    : [
                        ['+ Cash in', formatCurrency(report.cash_in)] as [string, string],
                        ['− Cash out', formatCurrency(report.cash_out)] as [string, string],
                      ]),
                ]}
                total={['Expected', formatCurrency(report.expected_cash)]}
                after={[
                  ['Counted', report.closing_balance === null ? '—' : formatCurrency(report.closing_balance)],
                  ['Difference', signed(report.discrepancy, formatCurrency), report.discrepancy ? (report.discrepancy < 0 ? 'text-error' : 'text-warning-dark') : undefined],
                ]}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={reprint}>
                <Printer /> Reprint
              </Button>
              <Button variant="outline" size="sm" onClick={exportCsv}>
                <Download /> Export
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
